import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SignUp as ClerkSignUp } from '@clerk/clerk-react';
import { setInviteToken } from '../hooks/useInviteClaim';

interface InviteInfo {
  brandName: string;
  role: string;
}

const ROLE_LABELS: Record<string, string> = {
  BRAND_MANAGER: 'Brand Manager',
  BRAND_ADMIN: 'Brand Admin',
  ADMIN: 'Admin',
  FINANCE: 'Finance',
};

export default function SignUp() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  useEffect(() => {
    if (!inviteToken) return;

    // Store token in sessionStorage for post-signup claim
    setInviteToken(inviteToken);

    // Validate token to show brand info
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    fetch(`${apiUrl}/webhooks/validate-invite/${encodeURIComponent(inviteToken)}`)
      .then((res) => {
        if (!res.ok) throw new Error('Invalid invite link');
        return res.json();
      })
      .then((data) => setInviteInfo(data))
      .catch(() => setInviteError('This invite link is invalid or has expired.'));
  }, [inviteToken]);

  return (
    <div className="flex min-h-screen">
      {/* Left Side - Branded Section */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-navy to-[#0a2f6b] p-12 flex-col justify-between">
        <div>
          <img
            src="https://static.wixstatic.com/media/28bda8_155c86a5b5f341c7839ba26980f0aea4~mv2.png/v1/fill/w_276,h_128,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/GB%20logo%20copy.png"
            alt="GlobalBridge"
            className="h-12 object-contain mix-blend-lighten brightness-200 mb-16"
          />

          <div className="text-white space-y-6">
            <h1 className="text-4xl font-bold leading-tight">
              Join GlobalBridge<br />Intelligence Suite
            </h1>
            <p className="text-xl text-blue-100">
              Start managing your e-commerce business with powerful analytics and insights.
            </p>

            {inviteInfo && (
              <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-5 mt-6">
                <p className="text-blue-100 text-sm font-medium uppercase tracking-wider mb-1">
                  You've been invited to join
                </p>
                <p className="text-white text-2xl font-bold">{inviteInfo.brandName}</p>
                <p className="text-blue-200 text-sm mt-1">
                  Role: {ROLE_LABELS[inviteInfo.role] || inviteInfo.role}
                </p>
              </div>
            )}

            <div className="mt-12 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Get Started in Minutes</h3>
                  <p className="text-sm text-blue-200">Quick setup with instant access to your dashboard</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Secure & Compliant</h3>
                  <p className="text-sm text-blue-200">Enterprise-grade security for your business data</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">All-in-One Platform</h3>
                  <p className="text-sm text-blue-200">Everything you need to manage and grow your business</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-blue-200 text-sm">
          &copy; 2025 GlobalBridge. All rights reserved.
        </div>
      </div>

      {/* Right Side - Sign Up Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo — navy chip so white logo is visible on light bg */}
          <div className="lg:hidden mb-8 flex justify-center">
            <div className="inline-flex items-center justify-center bg-brand-navy rounded-xl px-5 py-3">
              <img
                src="https://static.wixstatic.com/media/28bda8_155c86a5b5f341c7839ba26980f0aea4~mv2.png/v1/fill/w_276,h_128,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/GB%20logo%20copy.png"
                alt="GlobalBridge"
                className="h-10 object-contain mix-blend-lighten brightness-200"
              />
            </div>
          </div>

          {inviteInfo && (
            <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl lg:hidden">
              <p className="text-sm text-indigo-600 font-medium">You've been invited to join</p>
              <p className="text-lg font-bold text-indigo-900">{inviteInfo.brandName}</p>
            </div>
          )}

          {inviteError ? (
            <div className="space-y-4">
              <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-center">
                <p className="text-red-800 font-medium">{inviteError}</p>
                <p className="text-red-600 text-sm mt-2">
                  Please ask your admin for a new invite link.
                </p>
              </div>
              <div className="text-center">
                <a
                  href="/sign-up"
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium underline"
                >
                  Sign up without an invite
                </a>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Create your account</h2>
                <p className="text-gray-600">
                  {inviteInfo
                    ? `Complete your signup to join ${inviteInfo.brandName}`
                    : 'Start your journey with GlobalBridge today'}
                </p>
              </div>

              <ClerkSignUp
                fallbackRedirectUrl={inviteToken ? `/?invite=${inviteToken}` : '/'}
                appearance={{
                  variables: {
                    colorPrimary: '#031A42',
                    colorBackground: '#ffffff',
                    colorText: '#1f2937',
                    colorInputBackground: '#f9fafb',
                    colorInputText: '#1f2937',
                    borderRadius: '0.75rem',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  },
                  elements: {
                    rootBox: 'w-full',
                    card: 'shadow-xl border border-gray-200 rounded-xl',
                    headerTitle: 'hidden',
                    headerSubtitle: 'hidden',
                    socialButtonsBlockButton: 'border-gray-300 hover:bg-gray-50 transition-colors',
                    formButtonPrimary:
                      'bg-brand-navy hover:bg-[#052454] text-white font-semibold py-3 transition-all duration-200 shadow-md hover:shadow-lg',
                    formFieldInput:
                      'border-gray-300 focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 transition-all',
                    footerActionLink: 'text-brand-navy hover:text-[#052454] font-medium',
                    identityPreviewEditButton: 'text-brand-navy hover:text-[#052454]',
                    formFieldLabel: 'text-gray-700 font-medium',
                    dividerLine: 'bg-gray-300',
                    dividerText: 'text-gray-500',
                    otpCodeFieldInput: 'border-gray-300 focus:border-brand-navy',
                  },
                  layout: {
                    socialButtonsPlacement: 'bottom',
                    socialButtonsVariant: 'blockButton',
                  },
                }}
                routing="virtual"
                signInUrl="/sign-in"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
